/**
 * PWA Error Handler - Centralized error handling for PWA-related issues
 * Enhanced Error Handling 2025-07-09
 */

class PWAErrorHandler {
  constructor() {
    this.errorCounts = new Map();
    this.maxRetries = 3;
    this.setupGlobalErrorHandlers();
  }

  /**
   * Setup global error handlers for PWA-specific errors
   */
  setupGlobalErrorHandlers() {
    // Handle global JavaScript errors
    window.addEventListener('error', (event) => {
      this.handleError(event.error, 'global-error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(event.reason, 'unhandled-rejection');
      
      // Prevent default browser behavior for SW-related errors
      if (this.isServiceWorkerError(event.reason)) {
        event.preventDefault();
      }
    });

    // Handle service worker errors specifically
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('error', (event) => {
        this.handleError(event.error, 'service-worker-error');
      });
    }
  }

  /**
   * Check if an error is related to service workers
   */
  isServiceWorkerError(error) {
    if (!error) return false;
    
    const errorString = error.toString().toLowerCase();
    const errorStack = error.stack ? error.stack.toLowerCase() : '';
    
    return (
      errorString.includes('service worker') ||
      errorString.includes('sw.js') ||
      errorStack.includes('sw.js') ||
      error.name === 'SecurityError' ||
      error.name === 'NotSupportedError'
    );
  }

  /**
   * Handle different types of errors with appropriate recovery strategies
   */
  handleError(error, context, metadata = {}) {
    if (!error) return;

    const errorKey = `${context}-${error.name || 'unknown'}`;
    const errorCount = this.errorCounts.get(errorKey) || 0;
    
    // Increment error count
    this.errorCounts.set(errorKey, errorCount + 1);

    // Log error with context
    console.warn(`PWA Error Handler [${context}]:`, {
      error: error.toString(),
      errorCount: errorCount + 1,
      metadata,
      isServiceWorkerError: this.isServiceWorkerError(error),
      isPWA: window.matchMedia('(display-mode: standalone)').matches
    });

    // Apply recovery strategies based on error type
    if (this.isServiceWorkerError(error)) {
      this.handleServiceWorkerError(error, errorCount);
    } else if (error.name === 'ChunkLoadError' || error.name === 'Loading CSS chunk') {
      this.handleChunkLoadError(error, errorCount);
    } else if (error.name === 'NetworkError') {
      this.handleNetworkError(error, errorCount);
    }

    // If we've seen this error too many times, take more drastic action
    if (errorCount >= this.maxRetries) {
      this.handleCriticalError(error, context);
    }
  }

  /**
   * Handle service worker specific errors
   */
  handleServiceWorkerError(error, errorCount) {
    console.warn('Handling service worker error:', error);

    if (errorCount === 0) {
      // First attempt: try to update the service worker
      this.updateServiceWorker();
    } else if (errorCount === 1) {
      // Second attempt: unregister and re-register
      this.resetServiceWorker();
    } else if (errorCount >= 2) {
      // Final attempt: clear everything and reload
      this.clearAllCaches();
    }
  }

  /**
   * Handle chunk loading errors (common in PWAs)
   */
  handleChunkLoadError(error, errorCount) {
    console.warn('Handling chunk load error:', error);

    if (errorCount < 2) {
      // Try to reload the page to get fresh chunks
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  /**
   * Handle network errors
   */
  handleNetworkError(error, errorCount) {
    console.warn('Handling network error:', error);
    
    // For network errors, we typically don't need to take action
    // The app should handle offline states gracefully
  }

  /**
   * Handle critical errors that keep occurring
   */
  handleCriticalError(error, context) {
    console.error('Critical error detected:', error, 'Context:', context);
    
    // Show user-friendly error message
    this.showCriticalErrorMessage(error, context);
    
    // Clear everything and try to recover
    this.clearAllCaches();
  }

  /**
   * Update service worker
   */
  async updateServiceWorker() {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          console.log('Service worker updated successfully');
        }
      }
    } catch (error) {
      console.warn('Failed to update service worker:', error);
    }
  }

  /**
   * Reset service worker by unregistering and re-registering
   */
  async resetServiceWorker() {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        
        // Unregister all service workers
        await Promise.all(registrations.map(reg => reg.unregister()));
        
        // Wait a bit then re-register
        setTimeout(() => {
          navigator.serviceWorker.register('/sw.js')
            .then(() => console.log('Service worker re-registered'))
            .catch(error => console.warn('Failed to re-register service worker:', error));
        }, 1000);
      }
    } catch (error) {
      console.warn('Failed to reset service worker:', error);
    }
  }

  /**
   * Clear all caches and reload
   */
  async clearAllCaches() {
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }

      // Clear storage
      localStorage.clear();
      sessionStorage.clear();

      console.log('All caches cleared, reloading...');
      
      // Hard reload
      window.location.reload(true);
    } catch (error) {
      console.error('Failed to clear caches:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }

  /**
   * Show critical error message to user
   */
  showCriticalErrorMessage(error, context) {
    // Create a simple error notification
    const errorDiv = document.createElement('div');
    errorDiv.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ef4444;
        color: white;
        padding: 16px;
        border-radius: 8px;
        z-index: 10000;
        max-width: 300px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.1);
      ">
        <div style="font-weight: 600; margin-bottom: 8px;">App Error</div>
        <div style="margin-bottom: 12px;">The app encountered repeated errors. Clearing cache and reloading...</div>
        <button onclick="this.parentElement.remove()" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        ">Dismiss</button>
      </div>
    `;
    
    document.body.appendChild(errorDiv);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (errorDiv.parentElement) {
        errorDiv.remove();
      }
    }, 10000);
  }

  /**
   * Reset error counts (useful for testing)
   */
  resetErrorCounts() {
    this.errorCounts.clear();
  }
}

// Create singleton instance
const pwaErrorHandler = new PWAErrorHandler();

export default pwaErrorHandler;